/**
 * 場所編集シート
 *
 * 訪問カードタップ時に表示する場所名編集UI
 * - 既存のPlace編集
 * - 周辺検索（Google API）
 * - My Places（カスタム場所）の選択・割り当て
 * - 新規My Place作成
 * - 場所未設定の滞在では、場所を決めずにレシートだけ手で登録
 */

import { format } from 'date-fns'
import {
  ChevronDown,
  Database,
  Home,
  Loader2,
  MapPin,
  Plus,
  ReceiptText,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Wifi,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  assignPlaceToVisit,
  createMyPlace,
  createPlaceFromSearch,
  deleteVisit,
  getMyPlaces,
  getOverlandVisit,
  getPlaces,
  type NearbyPlaceResult,
  searchPlacesGoogle,
  updatePlace,
} from '@/api/services/overland'
import type { MyPlace, OverlandVisit, Place } from '@/api/types/overland'
import { VisitedPlaceBadge } from '@/components/shared/visited-place-badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { useOverlandStore } from '@/store/useOverlandStore'

import { GooglePlaceSelectDialog } from './google-place-select-dialog'

type Props = {
  visit: OverlandVisit
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 場所を決めずに、この滞在での買い物だけを手で登録する（場所未設定の滞在のみ表示） */
  onAddReceiptClick?: () => void
}

export const PlaceEditSheet = ({ visit, open, onOpenChange, onAddReceiptClick }: Props) => {
  const upsertVisit = useOverlandStore((s) => s.upsertVisit)
  const removeVisitFromStore = useOverlandStore((s) => s.removeVisit)
  const fetchVisits = useOverlandStore((s) => s.fetchVisits)

  const [customName, setCustomName] = useState('')
  const [radius, setRadius] = useState(50)
  const { isSubmitting: isSaving, run: runSave } = useSubmitLock()

  // My Places 関連
  const [myPlaces, setMyPlaces] = useState<MyPlace[]>([])
  const [isLoadingMyPlaces, setIsLoadingMyPlaces] = useState(false)
  const [selectedMyPlaceId, setSelectedMyPlaceId] = useState<number | null>(null)

  // 検索 関連（周辺検索 + 既存Place統合）
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchResults, setSearchResults] = useState<NearbyPlaceResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedSearchResult, setSelectedSearchResult] = useState<NearbyPlaceResult | null>(null)
  const [searchProvider, setSearchProvider] = useState<'google' | 'existing'>('google')

  // 新規作成
  const [newPlaceName, setNewPlaceName] = useState('')
  const [newPlaceRadius, setNewPlaceRadius] = useState(100)
  const [newPlaceWifiPatterns, setNewPlaceWifiPatterns] = useState('')
  const { isSubmitting: isCreating, run: runCreate } = useSubmitLock()

  // 既存Place検索
  const [existingPlaceKeyword, setExistingPlaceKeyword] = useState('')
  const [existingPlaceResults, setExistingPlaceResults] = useState<Place[]>([])
  const [isSearchingExisting, setIsSearchingExisting] = useState(false)
  const [selectedExistingPlaceId, setSelectedExistingPlaceId] = useState<number | null>(null)

  // Google再取得ダイアログ
  const [isReGeocodeDialogOpen, setIsReGeocodeDialogOpen] = useState(false)

  // 自動検索の初回実行を制御するref
  const initialGoogleSearchDone = useRef(false)
  const initialExistingSearchDone = useRef(false)

  // 最新の visit を取得して store に upsert する共通処理
  const refreshVisit = useCallback(async () => {
    try {
      const fresh = await getOverlandVisit(visit.id)
      upsertVisit(fresh)
    } catch {
      // 単一取得が失敗した場合は当日分を silent fetch にフォールバック
      try {
        const dateBase = visit.arrival_date ?? visit.timestamp
        const dateStr = dateBase ? format(new Date(dateBase), 'yyyy-MM-dd') : undefined
        await fetchVisits(dateStr, undefined, { silent: true })
      } catch {
        // ここも失敗したら諦める（次回ロードで反映）
      }
    }
  }, [visit.id, visit.arrival_date, visit.timestamp, upsertVisit, fetchVisits])

  // シートが閉じた→開いた時にstateを全リセット（stale state防止）
  useEffect(() => {
    if (open) {
      // 初期値を設定
      if (visit.place) {
        setCustomName(visit.place.custom_name ?? '')
        setRadius(visit.place.radius)
      } else {
        setCustomName('')
        setRadius(50)
      }
      // 検索state をリセット
      setSearchKeyword('')
      setSearchResults([])
      setSelectedSearchResult(null)
      setSearchProvider('google')
      setExistingPlaceKeyword('')
      setExistingPlaceResults([])
      setSelectedExistingPlaceId(null)
      setSelectedMyPlaceId(null)
      setNewPlaceName('')
      setNewPlaceRadius(100)
      initialGoogleSearchDone.current = false
      initialExistingSearchDone.current = false
    }
  }, [open, visit.place])

  // My Places を読み込み
  useEffect(() => {
    if (!open) return

    const loadMyPlaces = async () => {
      setIsLoadingMyPlaces(true)
      try {
        const places = await getMyPlaces()
        setMyPlaces(places)
      } catch {
        // My Places読み込み失敗は非致命的
      } finally {
        setIsLoadingMyPlaces(false)
      }
    }

    void loadMyPlaces()
  }, [open])

  // 新規作成時、WiFi名を初期値にセット
  useEffect(() => {
    if (open && visit.wifi) {
      setNewPlaceWifiPatterns(visit.wifi)
    }
  }, [open, visit.wifi])

  // 既存Place編集を保存
  const handleSave = useCallback(async () => {
    const placeId = visit.place_id
    if (!placeId) {
      toast.error('この訪問にはまだ場所が紐付けられていません')
      return
    }

    await runSave(async () => {
      try {
        await updatePlace(placeId, {
          custom_name: customName || null,
          radius,
        })
        toast.success('場所を更新しました')
        await refreshVisit()
        onOpenChange(false)
      } catch {
        toast.error('場所の更新に失敗しました')
      }
    })
  }, [visit.place_id, customName, radius, onOpenChange, refreshVisit, runSave])

  // 周辺検索（Google）
  const handleSearch = useCallback(async () => {
    setIsSearching(true)
    setSelectedSearchResult(null)
    try {
      // Google: キーワードあり→テキスト検索、なし→周辺検索
      const results = await searchPlacesGoogle(
        visit.latitude,
        visit.longitude,
        searchKeyword.trim() || undefined,
        5.0, // 5km以内
        20,
      )
      setSearchResults(results)
      if (results.length === 0) {
        toast.info('場所が見つかりませんでした')
      }
    } catch {
      toast.error('検索に失敗しました')
    } finally {
      setIsSearching(false)
    }
  }, [visit.latitude, visit.longitude, searchKeyword])

  // シート開いたときに自動で周辺検索（初回のみ）
  useEffect(() => {
    if (!open || initialGoogleSearchDone.current) return
    initialGoogleSearchDone.current = true
    const search = async () => {
      setIsSearching(true)
      setSelectedSearchResult(null)
      try {
        const results = await searchPlacesGoogle(visit.latitude, visit.longitude, undefined, 5.0, 20)
        setSearchResults(results)
        if (results.length === 0) {
          toast.info('場所が見つかりませんでした')
        }
      } catch {
        toast.error('検索に失敗しました')
      } finally {
        setIsSearching(false)
      }
    }
    void search()
  }, [open, visit.latitude, visit.longitude])

  // 検索結果からPlaceを作成して割り当て（通常のPlaceとして保存）
  const handleCreateFromSearchResult = useCallback(async () => {
    if (!selectedSearchResult) {
      toast.error('場所を選択してください')
      return
    }

    await runCreate(async () => {
      try {
        // Yahoo検索結果からPlaceを作成（Google APIで画像取得）
        const result = await createPlaceFromSearch(visit.id, selectedSearchResult)

        if (result.is_new) {
          toast.success(`「${result.name}」を作成して紐付けました`)
        } else {
          toast.success(`既存の場所「${result.name}」を紐付けました`)
        }
        // データ更新完了を待ってからシートを閉じる
        await refreshVisit()
        onOpenChange(false)
      } catch {
        toast.error('場所の作成に失敗しました')
      }
    })
  }, [selectedSearchResult, visit.id, onOpenChange, refreshVisit, runCreate])

  // My Placeを訪問に割り当て
  const handleAssignMyPlace = useCallback(async () => {
    if (!selectedMyPlaceId) {
      toast.error('場所を選択してください')
      return
    }

    await runSave(async () => {
      try {
        await assignPlaceToVisit(visit.id, selectedMyPlaceId)
        toast.success('場所を割り当てました')
        await refreshVisit()
        onOpenChange(false)
      } catch {
        toast.error('場所の割り当てに失敗しました')
      }
    })
  }, [visit.id, selectedMyPlaceId, onOpenChange, refreshVisit, runSave])

  // 新規My Placeを作成して割り当て
  const handleCreateAndAssign = useCallback(async () => {
    if (!newPlaceName.trim()) {
      toast.error('場所の名前を入力してください')
      return
    }

    await runCreate(async () => {
      try {
        // WiFiパターンをパース
        const wifiPatterns = newPlaceWifiPatterns
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean)

        // My Placeを作成
        const newPlace = await createMyPlace({
          name: newPlaceName.trim(),
          latitude: visit.latitude,
          longitude: visit.longitude,
          radius: newPlaceRadius,
          wifi_patterns: wifiPatterns.length > 0 ? wifiPatterns : undefined,
        })

        // 作成した場所を訪問に割り当て
        await assignPlaceToVisit(visit.id, newPlace.id)

        toast.success('新しい場所を作成して割り当てました')
        await refreshVisit()
        onOpenChange(false)
      } catch {
        toast.error('場所の作成に失敗しました')
      }
    })
  }, [newPlaceName, newPlaceRadius, newPlaceWifiPatterns, visit, onOpenChange, refreshVisit, runCreate])

  // 既存Place検索（名前検索または周辺検索）
  const handleSearchExistingPlaces = useCallback(async () => {
    setIsSearchingExisting(true)
    setSelectedExistingPlaceId(null)
    try {
      let results: Place[]
      if (existingPlaceKeyword.trim()) {
        // キーワードあり: 名前検索
        results = await getPlaces('name', 50, existingPlaceKeyword.trim())
      } else {
        // キーワードなし: 周辺1km以内のPlaceを距離順で取得
        results = await getPlaces('distance', 20, undefined, visit.latitude, visit.longitude, 1.0)
      }
      setExistingPlaceResults(results)
      if (results.length === 0) {
        toast.info('該当するPlaceが見つかりませんでした')
      }
    } catch {
      toast.error('検索に失敗しました')
    } finally {
      setIsSearchingExisting(false)
    }
  }, [existingPlaceKeyword, visit.latitude, visit.longitude])

  // 既存Placeタブ選択時に周辺Placeを自動取得（初回のみ）
  useEffect(() => {
    if (searchProvider !== 'existing' || initialExistingSearchDone.current) return
    initialExistingSearchDone.current = true
    const search = async () => {
      setIsSearchingExisting(true)
      setSelectedExistingPlaceId(null)
      try {
        const results = await getPlaces('distance', 20, undefined, visit.latitude, visit.longitude, 1.0)
        setExistingPlaceResults(results)
        if (results.length === 0) {
          toast.info('該当するPlaceが見つかりませんでした')
        }
      } catch {
        toast.error('検索に失敗しました')
      } finally {
        setIsSearchingExisting(false)
      }
    }
    void search()
  }, [searchProvider, visit.latitude, visit.longitude])

  // 既存Placeを訪問に割り当て
  const handleAssignExistingPlace = useCallback(async () => {
    if (!selectedExistingPlaceId) {
      toast.error('場所を選択してください')
      return
    }

    await runSave(async () => {
      try {
        await assignPlaceToVisit(visit.id, selectedExistingPlaceId)
        toast.success('場所を割り当てました')
        await refreshVisit()
        onOpenChange(false)
      } catch {
        toast.error('場所の割り当てに失敗しました')
      }
    })
  }, [visit.id, selectedExistingPlaceId, onOpenChange, refreshVisit, runSave])

  // Google再取得ダイアログで更新された時
  const handleReGeocodeUpdated = useCallback(async () => {
    await refreshVisit()
    onOpenChange(false)
  }, [refreshVisit, onOpenChange])

  // Visit削除
  const { isSubmitting: isDeleting, run: runDelete } = useSubmitLock()
  const handleDeleteVisit = useCallback(async () => {
    await runDelete(async () => {
      try {
        await deleteVisit(visit.id)
        toast.success('訪問を削除しました')
        removeVisitFromStore(visit.id)
        onOpenChange(false)
      } catch {
        toast.error('削除に失敗しました')
      }
    })
  }, [visit.id, onOpenChange, removeVisitFromStore, runDelete])

  const place = visit.place
  const autoName = place?.auto_name ?? '（自動取得中...）'

  return (
    <>
      <BottomSheet
        title="場所の編集"
        open={open}
        onOpenChange={onOpenChange}
        snapPoints={[0.85, 0.5]}
        defaultSnapPoint={0.85}
      >
        <div className="px-4 pb-4 h-full overflow-y-auto">
          <p className="text-sm text-muted-foreground mb-4">
            場所の名前をカスタマイズしたり、マイプレイスに割り当てることができます。
          </p>

          {!visit.place && onAddReceiptClick && (
            <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={onAddReceiptClick}>
              <ReceiptText className="size-4" />
              場所は決めずにレシートだけ登録
            </Button>
          )}

          <Tabs defaultValue="search" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="search">検索</TabsTrigger>
              <TabsTrigger value="custom">カスタム</TabsTrigger>
              <TabsTrigger value="delete">削除</TabsTrigger>
            </TabsList>

            {/* 検索タブ（周辺検索 + 既存Place統合） */}
            <TabsContent value="search" className="space-y-4 mt-4">
              {/* 検索モード切り替え */}
              <div className="flex gap-1">
                <Button
                  variant={searchProvider === 'google' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSearchProvider('google')
                    setSearchResults([])
                  }}
                  className="flex-1 text-xs px-2"
                >
                  Google検索
                </Button>
                <Button
                  variant={searchProvider === 'existing' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSearchProvider('existing')
                    setSearchResults([])
                    setExistingPlaceResults([])
                  }}
                  className="flex-1 text-xs px-2"
                >
                  <Database className="size-3 mr-1" />
                  既存Place
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                {searchProvider === 'google' && '周辺の場所を検索（キーワード任意）'}
                {searchProvider === 'existing' && '周辺1km以内の登録済みPlace（名前で絞り込み可）'}
              </p>

              {/* キーワード検索 */}
              <div className="flex gap-2">
                <Input
                  value={searchProvider === 'existing' ? existingPlaceKeyword : searchKeyword}
                  onChange={(e) =>
                    searchProvider === 'existing'
                      ? setExistingPlaceKeyword(e.target.value)
                      : setSearchKeyword(e.target.value)
                  }
                  placeholder={
                    searchProvider === 'existing' ? 'Place名で検索...' : 'キーワードで絞り込み（空欄で周辺検索）...'
                  }
                  onKeyDown={(e) =>
                    e.key === 'Enter' && (searchProvider === 'existing' ? handleSearchExistingPlaces() : handleSearch())
                  }
                />
                <Button
                  onClick={() => (searchProvider === 'existing' ? handleSearchExistingPlaces() : handleSearch())}
                  disabled={searchProvider === 'existing' ? isSearchingExisting : isSearching}
                  size="icon"
                  variant="outline"
                >
                  {(searchProvider === 'existing' ? isSearchingExisting : isSearching) ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Search className="size-4" />
                  )}
                </Button>
              </div>

              {/* 周辺検索結果（Yahoo/Google） */}
              {searchProvider !== 'existing' && (
                <>
                  {isSearching ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="size-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MapPin className="size-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">検索ボタンを押して周辺を検索</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {searchResults.map((result, idx) => (
                        <button
                          key={`${result.name}-${idx}`}
                          type="button"
                          onClick={() => setSelectedSearchResult(result)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedSearchResult?.name === result.name &&
                            selectedSearchResult?.latitude === result.latitude
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:bg-accent'
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <div className="min-w-0 truncate font-medium">{result.name}</div>
                            <VisitedPlaceBadge visitCount={result.visit_count} lastVisitedAt={result.last_visited_at} />
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            {result.distance_m !== null && <span>{Math.round(result.distance_m)}m</span>}
                            {result.category && <span>{result.category}</span>}
                          </div>
                          {result.address && <div className="text-xs text-muted-foreground mt-1">{result.address}</div>}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 選択した場所の設定 */}
                  {selectedSearchResult && (
                    <div className="space-y-3 pt-3 border-t">
                      <p className="text-sm font-medium">「{selectedSearchResult.name}」を設定</p>
                      {selectedSearchResult.address && (
                        <p className="text-xs text-muted-foreground">{selectedSearchResult.address}</p>
                      )}

                      <Button onClick={handleCreateFromSearchResult} disabled={isCreating} className="w-full">
                        {isCreating ? (
                          <>
                            <Loader2 className="size-4 mr-2 animate-spin" />
                            作成中...
                          </>
                        ) : (
                          <>
                            <MapPin className="size-4 mr-2" />
                            この場所を設定
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* 既存Place検索結果 */}
              {searchProvider === 'existing' && (
                <>
                  {isSearchingExisting ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="size-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : existingPlaceResults.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Database className="size-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">周辺に登録済みPlaceがありません</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {existingPlaceResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setSelectedExistingPlaceId(p.id)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedExistingPlaceId === p.id
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:bg-accent'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="size-4 text-muted-foreground shrink-0" />
                            <span className="font-medium truncate">{p.display_name}</span>
                            {p.distance_km !== undefined && p.distance_km !== null && (
                              <span className="text-xs text-muted-foreground ml-auto shrink-0">
                                {p.distance_km < 1
                                  ? `${Math.round(p.distance_km * 1000)}m`
                                  : `${p.distance_km.toFixed(1)}km`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>訪問: {p.visit_count}回</span>
                            {p.categories.length > 0 && (
                              <span>{p.categories[0].display_name ?? p.categories[0].name}</span>
                            )}
                          </div>
                          {p.address && <div className="text-xs text-muted-foreground mt-1 truncate">{p.address}</div>}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 既存Place割り当てボタン */}
                  {selectedExistingPlaceId && (
                    <Button onClick={handleAssignExistingPlace} disabled={isSaving} className="w-full">
                      {isSaving ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          割り当て中...
                        </>
                      ) : (
                        <>
                          <MapPin className="size-4 mr-2" />
                          選択したPlaceに割り当て
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </TabsContent>

            {/* カスタムタブ（編集・マイプレイス・新規作成を統合） */}
            <TabsContent value="custom" className="space-y-4 mt-4">
              {/* セクション1: この場所を編集（Placeがある場合のみ） */}
              {visit.place_id && (
                <Collapsible defaultOpen className="border rounded-lg">
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-accent rounded-t-lg [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-2 font-medium">
                      <Save className="size-4" />
                      この場所を編集
                    </div>
                    <ChevronDown className="size-4 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-3 pt-0 space-y-3">
                    {/* Yahooで取得した場合のGoogle再取得ボタン */}
                    {place?.provider === 'yahoo' && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2">
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          この場所はYahoo APIで取得されました。Googleで再取得すると、画像や詳細情報が充実します。
                        </p>
                        <Button
                          onClick={() => setIsReGeocodeDialogOpen(true)}
                          size="sm"
                          variant="outline"
                          className="w-full border-amber-500/50 hover:bg-amber-500/20"
                        >
                          <RefreshCw className="size-4 mr-2" />
                          Googleで再取得
                        </Button>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-xs">自動取得名: {autoName}</Label>
                      <Input
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="カスタム名を入力..."
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">半径</Label>
                        <span className="text-xs text-muted-foreground">{radius}m</span>
                      </div>
                      <Slider value={[radius]} onValueChange={([v]) => setRadius(v)} min={10} max={500} step={10} />
                    </div>
                    <Button onClick={handleSave} disabled={isSaving} size="sm" className="w-full">
                      {isSaving ? <Loader2 className="size-4 animate-spin" /> : '保存'}
                    </Button>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* セクション2: マイプレイスから選択 */}
              <Collapsible defaultOpen={myPlaces.length > 0 && !visit.place_id} className="border rounded-lg">
                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-accent rounded-t-lg [&[data-state=open]>svg]:rotate-180">
                  <div className="flex items-center gap-2 font-medium">
                    <Home className="size-4" />
                    マイプレイスから選択
                    {myPlaces.length > 0 && (
                      <span className="text-xs text-muted-foreground">({myPlaces.length}件)</span>
                    )}
                  </div>
                  <ChevronDown className="size-4 transition-transform" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-3 pt-0 space-y-3">
                  {isLoadingMyPlaces ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : myPlaces.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">マイプレイスがありません</p>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {myPlaces.map((myPlace) => (
                          <button
                            key={myPlace.id}
                            type="button"
                            onClick={() => setSelectedMyPlaceId(myPlace.id)}
                            className={`w-full text-left p-2 rounded-lg border text-sm transition-colors ${
                              selectedMyPlaceId === myPlace.id
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:bg-accent'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Home className="size-3 text-muted-foreground" />
                              <span className="font-medium">{myPlace.name}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <span>{myPlace.radius}m</span>
                              <span>訪問{myPlace.visit_count}回</span>
                              {myPlace.wifi_patterns.length > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <Wifi className="size-3" />
                                  {myPlace.wifi_patterns.length}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                      <Button
                        onClick={handleAssignMyPlace}
                        disabled={isSaving || !selectedMyPlaceId}
                        size="sm"
                        className="w-full"
                      >
                        {isSaving ? <Loader2 className="size-4 animate-spin" /> : '割り当てる'}
                      </Button>
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* セクション3: 新規マイプレイス作成 */}
              <Collapsible className="border rounded-lg">
                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-accent rounded-t-lg [&[data-state=open]>svg]:rotate-180">
                  <div className="flex items-center gap-2 font-medium">
                    <Plus className="size-4" />
                    新規マイプレイス作成
                  </div>
                  <ChevronDown className="size-4 transition-transform" />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-3 pt-0 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="new-place-name" className="text-sm">
                      場所の名前
                    </Label>
                    <Input
                      id="new-place-name"
                      value={newPlaceName}
                      onChange={(e) => setNewPlaceName(e.target.value)}
                      placeholder="例: 自宅、オフィス"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">半径</Label>
                      <span className="text-xs text-muted-foreground">{newPlaceRadius}m</span>
                    </div>
                    <Slider
                      value={[newPlaceRadius]}
                      onValueChange={([v]) => setNewPlaceRadius(v)}
                      min={10}
                      max={500}
                      step={10}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wifi-patterns" className="text-sm flex items-center gap-1">
                      <Wifi className="size-3" />
                      WiFiパターン
                    </Label>
                    <Input
                      id="wifi-patterns"
                      value={newPlaceWifiPatterns}
                      onChange={(e) => setNewPlaceWifiPatterns(e.target.value)}
                      placeholder="カンマ区切り"
                    />
                  </div>
                  <Button
                    onClick={handleCreateAndAssign}
                    disabled={isCreating || !newPlaceName.trim()}
                    size="sm"
                    className="w-full"
                  >
                    {isCreating ? <Loader2 className="size-4 animate-spin" /> : '作成して割り当て'}
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            </TabsContent>

            {/* 削除タブ */}
            <TabsContent value="delete" className="space-y-4 mt-4">
              <div className="p-4 border rounded-lg bg-destructive/5 border-destructive/20">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="size-5 text-destructive" />
                  <span className="font-medium">この訪問を削除</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  この訪問データを完全に削除します。
                  {place && <>（場所「{place.display_name}」は残ります）</>}
                </p>
                <div className="text-xs text-muted-foreground mb-3 space-y-1">
                  <p>到着: {visit.arrival_date ? new Date(visit.arrival_date).toLocaleString('ja-JP') : '不明'}</p>
                  <p>
                    出発: {visit.departure_date ? new Date(visit.departure_date).toLocaleString('ja-JP') : '滞在中'}
                  </p>
                </div>
                <Button variant="destructive" onClick={handleDeleteVisit} disabled={isDeleting} className="w-full">
                  {isDeleting ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      削除中...
                    </>
                  ) : (
                    <>
                      <Trash2 className="size-4 mr-2" />
                      訪問を削除
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-destructive text-center">※ この操作は取り消せません</p>
            </TabsContent>
          </Tabs>

          {/* 共通情報 */}
          <div className="mt-4 pt-4 border-t text-xs text-muted-foreground space-y-1">
            <p>
              座標: {visit.latitude.toFixed(6)}, {visit.longitude.toFixed(6)}
            </p>
            {visit.wifi && <p>WiFi: {visit.wifi}</p>}
            {place && <p>訪問回数: {place.visit_count}回</p>}
          </div>
        </div>
      </BottomSheet>

      {/* Google再取得ダイアログ */}
      {place && (
        <GooglePlaceSelectDialog
          place={place}
          open={isReGeocodeDialogOpen}
          onOpenChange={setIsReGeocodeDialogOpen}
          onUpdated={handleReGeocodeUpdated}
        />
      )}
    </>
  )
}
